/* Story 12.10 AC2 + 13.13 AC2 apus-ami-agent (D-105) — "Kênh nhận thông báo" trong Hub: mỗi kênh CHAT mà môi trường đã nối
   notification-api một dòng ĐÃ / CHƯA liên kết; kênh tôi CHỌN trong tuỳ chọn thông báo mà chưa liên kết ⇒ NHẮC (NTF bỏ qua tin tới
   tài khoản chưa liên kết — D-NTFCH-04). "Liên kết" ⇒ mã MỘT LẦN (≤ 10 phút) + hướng dẫn gửi cho bot/OA của tổ chức; "Gỡ" ⇒ DELETE.
   Hợp đồng: ami-agent-v1@1.1.0-draft `amiGetNotificationPrefs` (`available.channels`, `prefs.channels`) · `amiListChannelLinks`
   (`NtfChannelLinkList`) · `amiCreateChannelLinkCode` (`NtfChannelLinkCode`) · `amiDeleteChannelLink` (chỉ Java). Server demo Node ⛔
   có các đường ấy ⇒ lỗi ⇒ danh sách rỗng ⇒ Hub ẩn mục. Chỉ `channel`, `external_mask`, `linked_at` của liên kết được giữ — ⛔ chat id /
   `external_id` thô lên màn hình, kể cả khi server lỡ trả. */
import { el } from "../ui/dom.ts";
import type { HubApi } from "./erp-links.ts";

export type ChatChannel = "telegram" | "zalo" | "teams" | "apus_chat";
/** = `NtfChatChannel` của hợp đồng, theo thứ tự hiển thị. */
export const CHAT_CHANNELS: readonly ChatChannel[] = ["telegram", "zalo", "teams", "apus_chat"];
const LABEL: Record<ChatChannel, string> = { telegram: "Telegram", zalo: "Zalo", teams: "Microsoft Teams", apus_chat: "Apus Chat" };

export interface ChannelRow {
  channel: ChatChannel;
  label: string;
  linked: boolean;
  mask: string | null;
  linkedAt: string | null;
  /** Tôi đã chọn kênh này cho ít nhất một loại thông báo. */
  chosen: boolean;
}

export interface LinkCode {
  channel: ChatChannel;
  code: string;
  expiresAt: string;
  botRef: string;
  deepLink: string | null;
}

const isChat = (v: unknown): v is ChatChannel => typeof v === "string" && (CHAT_CHANNELS as readonly string[]).includes(v);
export const channelLabel = (c: ChatChannel): string => LABEL[c] || c;

/** Gộp `GET /notification-prefs` + `GET /channel-links` thành các dòng. Thân sai hình ⇒ ném (Hub ẩn mục, ⛔ đoán trạng thái). */
export function buildChannelRows(prefsBody: unknown, linksBody: unknown): ChannelRow[] {
  const env = prefsBody as { prefs?: { channels?: Record<string, unknown> }; available?: { channels?: unknown } } | null;
  const avail = env?.available?.channels;
  if (!Array.isArray(avail)) throw new Error("notification-prefs sai hình");
  const links = (linksBody as { links?: unknown } | null)?.links;
  if (!Array.isArray(links)) throw new Error("channel-links sai hình");
  const chosen = new Set<string>();
  for (const v of Object.values(env?.prefs?.channels || {})) if (Array.isArray(v)) for (const c of v) if (typeof c === "string") chosen.add(c);
  const byChannel = new Map<string, { mask: string | null; linkedAt: string | null }>();
  for (const x of links) {
    const r = x as { channel?: unknown; external_mask?: unknown; linked_at?: unknown } | null;
    if (!r || !isChat(r.channel)) continue;
    byChannel.set(r.channel, {
      mask: typeof r.external_mask === "string" && r.external_mask ? r.external_mask.slice(0, 40) : null,
      linkedAt: typeof r.linked_at === "string" ? r.linked_at : null,
    });
  }
  const offered = new Set(avail.filter(isChat));
  /* chỉ kênh môi trường ĐÃ nối NTF (`available.channels`); kênh ở `later` ⇒ ⛔ dòng dù server có trả liên kết */
  return CHAT_CHANNELS.filter((c) => offered.has(c)).map((c) => {
    const l = byChannel.get(c);
    return { channel: c, label: channelLabel(c), linked: !!l, mask: l?.mask ?? null, linkedAt: l?.linkedAt ?? null, chosen: chosen.has(c) };
  });
}

/** Tải hai nguồn; mọi lỗi (Node demo 404, 422/503 môi trường chưa nối NTF, thân hỏng) ⇒ rỗng — Hub chỉ ẩn mục. */
export async function loadChannelRows(api: HubApi): Promise<ChannelRow[]> {
  try {
    const [prefs, links] = await Promise.all([api<unknown>("GET", "/notification-prefs"), api<unknown>("GET", "/channel-links")]);
    return buildChannelRows(prefs, links);
  } catch {
    return [];
  }
}

/** Số kênh tôi CHỌN mà chưa liên kết — chấm nhắc trên thanh trên. */
export function needsLinkReminder(rows: ChannelRow[]): number {
  return rows.filter((r) => r.chosen && !r.linked).length;
}

const CODE_RE = /^[0-9A-HJKMNP-TV-Z]{8}$/;
const BOT_RE = /^[A-Za-z0-9._:@-]{1,128}$/;

/** Lọc `NtfChannelLinkCode` — mã / bot ⛔ đúng hình ⇒ ném; `deep_link` chỉ nhận https. */
export function normalizeLinkCode(body: unknown, channel: ChatChannel): LinkCode {
  const r = body as { code?: unknown; expires_at?: unknown; bot_ref?: unknown; deep_link?: unknown } | null;
  const code = typeof r?.code === "string" && CODE_RE.test(r.code) ? r.code : null;
  const botRef = typeof r?.bot_ref === "string" && BOT_RE.test(r.bot_ref) ? r.bot_ref : null;
  const expiresAt = typeof r?.expires_at === "string" && !Number.isNaN(Date.parse(r.expires_at)) ? r.expires_at : null;
  if (!code || !botRef || !expiresAt) throw new Error("máy chủ trả mã liên kết không hợp lệ");
  let deepLink: string | null = null;
  if (typeof r?.deep_link === "string") {
    try {
      const u = new URL(r.deep_link);
      deepLink = u.protocol === "https:" ? u.href : null;
    } catch {
      deepLink = null;
    }
  }
  return { channel, code, expiresAt, botRef, deepLink };
}

export async function createLinkCode(api: HubApi, channel: ChatChannel): Promise<LinkCode> {
  return normalizeLinkCode(await api<unknown>("POST", "/channel-links/" + encodeURIComponent(channel) + "/codes", {}), channel);
}

export async function unlinkChannel(api: HubApi, channel: ChatChannel): Promise<void> {
  await api("DELETE", "/channel-links/" + encodeURIComponent(channel));
}

/** Câu báo lỗi theo mã của hợp đồng (409 `channel_not_enabled`, 422/503 `channel_unavailable`, 404 `channel_link_not_found`). */
export function channelErrorText(err: unknown, verb: string): string {
  const code = (err as { code?: string } | null)?.code;
  if (code === "channel_not_enabled") return verb + ": tổ chức chưa bật kênh này — nhờ quản trị bật trong Console (Kênh thông báo).";
  if (code === "channel_unavailable") return verb + ": dịch vụ thông báo tạm chưa sẵn sàng, thử lại sau.";
  if (code === "channel_link_not_found") return verb + ": kênh này chưa liên kết.";
  return verb + ": " + ((err as Error | null)?.message || "lỗi không rõ");
}

const VN_MS = 7 * 3600e3;
const hhmm = (iso: string): string => {
  const d = new Date(Date.parse(iso) + VN_MS);
  return String(d.getUTCHours()).padStart(2, "0") + ":" + String(d.getUTCMinutes()).padStart(2, "0");
};

function codeBox(c: LinkCode, label: string): HTMLElement {
  const box = el("div", "apx-chncode", { "data-chn-code": c.channel });
  const code = el("strong", "apx-chncode-code");
  code.textContent = c.code;
  const how = el("p", "apx-aside-hint");
  how.textContent = c.channel === "telegram"
    ? "Mở Telegram, gửi /start " + c.code + " cho bot @" + c.botRef.replace(/^@/, "") + "."
    : "Gửi mã " + c.code + " cho tài khoản " + c.botRef + " của tổ chức trên " + label + ".";
  const exp = el("p", "apx-aside-foot");
  exp.textContent = "Mã dùng một lần, hết hạn lúc " + hhmm(c.expiresAt) + " (tối đa 10 phút).";
  box.append(code, how);
  if (c.deepLink) {
    const a = el("a", "apx-link", { href: c.deepLink, target: "_blank", rel: "noopener noreferrer" });
    a.textContent = "Mở " + label;
    box.append(a);
  }
  box.append(exp);
  return box;
}

/** Popover "Kênh nhận thông báo". Nút mang `data-chn-act` (link|unlink) + `data-chn` — Hub bắt bằng listener chung của thanh trên. */
export function renderChannels(rows: ChannelRow[], opts: { busy?: string | null; note?: string | null; codes?: Partial<Record<ChatChannel, LinkCode>> } = {}): HTMLElement {
  const pop = el("div", "apx-bellpop apx-erppop apx-chnpop", { role: "dialog", "aria-label": "Kênh nhận thông báo", "data-chn-pop": "" });
  const head = el("div", "apx-bellhead");
  const title = el("strong");
  title.textContent = "Kênh nhận thông báo";
  head.append(title);
  const hint = el("p", "apx-aside-hint");
  hint.textContent = "Liên kết tài khoản chat của bạn để nhận thông báo của Ami ngoài Hub. Chưa liên kết thì Ami bỏ qua kênh đó.";
  pop.append(head, hint);
  for (const r of rows) {
    const row = el("div", "apx-notif apx-erprow", { "data-chn-row": r.channel });
    const body = el("span", "apx-notif-body");
    const name = el("span", "apx-notif-title");
    name.textContent = r.label;
    const state = el("span", "apx-item-state apx-tone-" + (r.linked ? "ok" : r.chosen ? "warn" : "info"));
    state.textContent = r.linked ? "Đã liên kết" + (r.mask ? " · " + r.mask : "") : "Chưa liên kết";
    body.append(name, state);
    const act = r.linked ? "unlink" : "link";
    const b = el("button", "apx-btn apx-sm " + (r.linked ? "apx-btn-ghost" : "apx-btn-primary"), { type: "button", "data-chn-act": act, "data-chn": r.channel });
    b.textContent = opts.busy === r.channel ? "Đang xử lý…" : r.linked ? "Gỡ" : "Liên kết";
    if (opts.busy) b.setAttribute("disabled", "");
    row.append(body, b);
    pop.append(row);
    if (r.chosen && !r.linked) {
      const remind = el("p", "apx-aside-hint apx-tone-warn", { "data-chn-remind": r.channel });
      remind.textContent = "Bạn đã chọn nhận thông báo qua " + r.label + " nhưng chưa liên kết tài khoản — bấm \"Liên kết\" để bắt đầu nhận.";
      pop.append(remind);
    }
    const c = opts.codes?.[r.channel];
    if (c && !r.linked && Date.parse(c.expiresAt) > Date.now()) pop.append(codeBox(c, r.label));
  }
  if (opts.note) {
    const n = el("p", "apx-aside-foot");
    n.textContent = opts.note;
    pop.append(n);
  }
  return pop;
}
